import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import UserIcon from "../../assets/icons/UserIcon";
import { selectCateringCheckoutCart, selectCateringCartPackages, selectPendingPayment, selectIsPastOrderRestoration, selectIsModifyOrderMode, selectIsUnpaidOrderMode, selectCateringRestaurantId } from "../../redux/features/restaurants/restaurantSelectors";
import { selectServiceType } from "../../redux/features/customer/customerSelectors";
import { replaceCateringCartWithSinglePackage, setCateringCartFilters, clearCateringCart, clearPendingPayment } from "../../redux/features/restaurants/restaurantSlice";
import { calculatePackageLabels } from "../../utils/paxCalculations";
import PendingPaymentWarningModal, { usePendingPaymentCheck } from "../modal/PendingPaymentWarningModal";

const PackageGrid = ({ packages, onPackageSelect, filters }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const checkoutCart = useSelector(selectCateringCheckoutCart);
  const cateringCartPackages = useSelector(selectCateringCartPackages);
  const pendingPayment = useSelector(selectPendingPayment);
  const serviceType = useSelector(selectServiceType);
  const isPastOrderRestoration = useSelector(selectIsPastOrderRestoration);
  const isModifyOrderMode = useSelector(selectIsModifyOrderMode);
  const isUnpaidOrderMode = useSelector(selectIsUnpaidOrderMode);
  const cateringRestaurantId = useSelector(selectCateringRestaurantId);
  
  console.log("PackageGrid received packages:", packages);
  console.log("PackageGrid packages length:", packages?.length);
  console.log("PackageGrid filters:", filters);

  // State for confirmation popup
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [selectedPackageForConfirmation, setSelectedPackageForConfirmation] = useState(null);
  const [warningType, setWarningType] = useState(null); // 'different-package', 'different-brand', 'past-order', 'modify-order', 'unpaid-order'
  
  // Use the pending payment check hook
  const { checkAndProceed, showWarning: showPendingPaymentWarning, handleProceed: handleConfirmPendingPayment, handleCancel: handleCancelPendingPayment } = usePendingPaymentCheck();
  
  // Check if all required filter fields are filled (only date required on packages screen)
  const areFiltersComplete = filters && filters.date;
  
  const handleBackClick = () => {
    // Navigate to catering order page
    navigate("/catering-order");
  };
  
  const handlePackageClick = async (pkg) => {
    // Only allow interaction if filters are complete and package is not disabled
    if (!pkg.isDisabled && areFiltersComplete) {
      console.log('📦 Package clicked:', pkg.title, 'ID:', pkg.id);
      console.log('📦 Current state:', {
        isPastOrderRestoration,
        isModifyOrderMode,
        isUnpaidOrderMode,
        cateringRestaurantId,
        clickedPackageRestaurantId: pkg.restaurant_id
      });
      
      // Check for pending payment with API verification
      const canProceed = await checkAndProceed(async () => {
        console.log('Proceeding with package selection after confirmation');
        handlePackageSelection(pkg);
      });
      
      // If canProceed is true, continue with normal flow; otherwise modal will show
      if (!canProceed) {
        setSelectedPackageForConfirmation(pkg);
        return;
      }
      
      handlePackageSelection(pkg);
    }
  };
  
  const handlePackageSelection = (pkg) => {
    // ALWAYS clear modify/unpaid order mode flags when selecting a package from grid
    // This ensures fresh orders don't show "Modify & Proceed to Checkout" button
    const needsFlagsClear = isPastOrderRestoration || isModifyOrderMode || isUnpaidOrderMode;
    
    // Check if there are any packages in the catering cart (both checkout cart and working cart)
    const hasPackagesInCart = (checkoutCart && checkoutCart.length > 0) || (cateringCartPackages && cateringCartPackages.length > 0);
    
    if (hasPackagesInCart) {
        // Check if the clicked package is already in the cart (either checkout or working cart)
        const isPackageInCheckoutCart = checkoutCart && checkoutCart.some(cartPkg => cartPkg.id === pkg.id);
        const isPackageInWorkingCart = cateringCartPackages && cateringCartPackages.some(cartPkg => cartPkg.id === pkg.id);
        
        if (isPackageInCheckoutCart || isPackageInWorkingCart) {
          // Same package is already in cart - check if it's from a past order or different brand
          const isDifferentBrand = cateringRestaurantId && pkg.restaurant_id && cateringRestaurantId !== pkg.restaurant_id;
          
          if (isDifferentBrand) {
            // Same package but from different brand/restaurant
            console.log('⚠️ Same package but different brand');
            setWarningType('different-brand');
            setSelectedPackageForConfirmation(pkg);
            setShowConfirmation(true);
          } else if (needsFlagsClear) {
            // Package is from a past order (modify/unpaid/continue payment)
            console.log('⚠️ Package from past order');
            let type = 'past-order';
            if (isModifyOrderMode) type = 'modify-order';
            else if (isUnpaidOrderMode) type = 'unpaid-order';
            
            setWarningType(type);
            setSelectedPackageForConfirmation(pkg);
            setShowConfirmation(true);
          } else {
            // Same package, same brand, not from past order - proceed normally without clearing
            console.log('✅ Same package, proceeding without clearing cart');
            proceedWithPackageSelection(pkg, false); // Pass false to not clear cart
          }
        } else {
          // Different package selected, show confirmation popup
          console.log('⚠️ Different package selected');
          setWarningType('different-package');
          setSelectedPackageForConfirmation(pkg);
          setShowConfirmation(true);
        }
      } else {
        // No packages in cart
        if (needsFlagsClear) {
          // Clear flags if coming from modify/unpaid order mode
          console.log('✅ No packages in cart, but need to clear modify/unpaid flags');
          proceedWithPackageSelection(pkg, true); // Clear cart (and flags)
        } else {
          // Fresh start, proceed normally
          console.log('✅ No packages in cart, fresh start');
          proceedWithPackageSelection(pkg, false); // No need to clear empty cart
        }
      }
  };

  const proceedWithPackageSelection = (pkg, shouldClearCart = true) => {
    if (shouldClearCart) {
      // Clear any existing catering cart packages from past orders (modify order or continue payment)
      // This ensures we start fresh when selecting a package from the grid
      // clearCateringCart() handles all Redux flags: modify mode, past order restoration, address, etc.
      console.log('🧹 Clearing cart before proceeding');
      dispatch(clearCateringCart());
      
      // Reset to today's date and clear time when starting fresh (keep PAX from FilterBar)
      const today = new Date().toISOString().split('T')[0];
      dispatch(setCateringCartFilters({ 
        date: today, 
        time: "", 
        pax: filters?.pax || 20 
      }));
    } else {
      console.log('✅ Keeping existing cart');
      // Keep current filters (date and time selections) only when not clearing
      dispatch(setCateringCartFilters(filters));
    }
    
    // Call the original onPackageSelect if provided, pass the complete package object
    if (onPackageSelect) {
      onPackageSelect(pkg);
    }
    // Navigate to package detail page
    navigate(`/catering-package/${pkg.id}`);
  };

  const handleConfirmPackageChange = () => {
    if (selectedPackageForConfirmation) {
      // Clear all existing packages and cart data
      dispatch(clearCateringCart());
      
      // Reset date and time when switching packages (reset to today's date for fresh start)
      const today = new Date().toISOString().split('T')[0];
      dispatch(setCateringCartFilters({ 
        date: today, 
        time: "", 
        pax: filters?.pax || 20 
      }));
      
      // Proceed with new package selection (clear cart)
      proceedWithPackageSelection(selectedPackageForConfirmation, true);
      
      // Close confirmation modal
      setShowConfirmation(false);
      setSelectedPackageForConfirmation(null);
      setWarningType(null);
    }
  };

  const handleCancelPackageChange = () => {
    setShowConfirmation(false);
    setSelectedPackageForConfirmation(null);
    setWarningType(null);
  };


  // Get modal content based on warning type
  const getModalContent = () => {
    if (!selectedPackageForConfirmation) return null;
    
    switch (warningType) {
      case 'different-package':
        return {
          title: 'Replace Current Package?',
          message: `You already have a package in your cart. Only one package can be selected at a time. Selecting "${selectedPackageForConfirmation.title}" will clear your current package and all its items. Are you sure you want to proceed?`,
          confirmText: 'Yes, Replace Package',
          confirmClass: 'bg-red-600 hover:bg-red-700'
        };
      
      case 'different-brand':
        return {
          title: 'Switch to Different Restaurant?',
          message: `This will switch your order to a different restaurant. Your current cart for "${selectedPackageForConfirmation.title}" will be cleared. Are you sure you want to proceed?`,
          confirmText: 'Yes, Switch Restaurant',
          confirmClass: 'bg-orange-600 hover:bg-orange-700'
        };
      
      case 'past-order':
        return {
          title: 'Start Fresh Order?',
          message: `You are currently viewing a past order. Selecting "${selectedPackageForConfirmation.title}" from the package grid will clear the current restored order and start a fresh order. Are you sure you want to proceed?`,
          confirmText: 'Yes, Start Fresh',
          confirmClass: 'bg-blue-600 hover:bg-blue-700'
        };
      
      case 'modify-order':
        return {
          title: 'Exit Modify Order Mode?',
          message: `You are currently modifying a paid order. Selecting "${selectedPackageForConfirmation.title}" will exit modify mode and discard any changes. Are you sure you want to proceed?`,
          confirmText: 'Yes, Exit Modify Mode',
          confirmClass: 'bg-amber-600 hover:bg-amber-700'
        };
      
      case 'unpaid-order':
        return {
          title: 'Discard Unpaid Order?',
          message: `You are currently viewing an unpaid order. Selecting "${selectedPackageForConfirmation.title}" will discard the current order and start fresh. Are you sure you want to proceed?`,
          confirmText: 'Yes, Discard Order',
          confirmClass: 'bg-purple-600 hover:bg-purple-700'
        };
      
      default:
        return {
          title: 'Confirm Action',
          message: 'Are you sure you want to proceed?',
          confirmText: 'Yes, Proceed',
          confirmClass: 'bg-gray-600 hover:bg-gray-700'
        };
    }
  };

  return (
    <>
      {/* Back Button */}
      <div className="mb-4">
        <button 
          className="p-2"
          onClick={handleBackClick}
        >
          <img src="/Images/Menu/Bag.png" alt="Back" />
        </button>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
    {packages.map(pkg => (
      <div
        key={pkg.id}
        className={`flex flex-col items-center cursor-pointer ${
          pkg.isDisabled || !areFiltersComplete ? 'opacity-50 pointer-events-none' : ''
        }`}
        onClick={() => handlePackageClick(pkg)}
      >
        {/* Image section */}
        <div className="w-full rounded-2xl overflow-hidden relative z-20" style={{ aspectRatio: '327/120' }}>
          <img src={pkg.image} alt={pkg.title} className={`w-full h-full object-cover rounded-2xl`} />
          
          {/* Special badges */}
          <div className="absolute top-3 left-3 flex flex-col gap-1">
            {pkg.isTopPick && (
              <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full font-bold">
                TOP PICK
              </span>
            )}
            {pkg.isNew && (
              <span className="bg-green-500 text-white text-xs px-2 py-1 rounded-full font-bold">
                NEW
              </span>
            )}
            {pkg.isPopular && (
              <span className="bg-orange-500 text-white text-xs px-2 py-1 rounded-full font-bold">
                POPULAR
              </span>
            )}
          </div>
          
          {/* People count badge */}
          <div className="absolute top-3 right-3 bg-white bg-opacity-90 rounded-xl px-3 py-1 text-xs font-bold text-blue-900 flex items-center gap-1 shadow border border-blue-900">
          <UserIcon className="w-3 h-3" fill="currentColor" />
            {pkg.servePeople || '10 - 100'}
          </div>
        </div>
        {/* Content section */}
        <div className="relative z-10 -mt-1 w-[96%] bg-white rounded-b-2xl shadow-lg flex flex-col px-6 py-4" style={{ maxWidth: 605 }}>
                      <div className="pr-12">
              <div className="font-bold text-gray-700 text-lg mb-1">{pkg.title}</div>
              {/* <div className="text-sm text-gray-500 mb-2 line-clamp-2">
                {(() => {
                  // Calculate dynamic labels based on PAX and pax_limit data
                  const dynamicLabels = calculatePackageLabels(pkg.paxLimit, filters?.pax);
                  // Fall back to static content if no dynamic labels or filters incomplete
                  return dynamicLabels || pkg.food || pkg.description || '2x Appetizers, 2x Pasta, 2x Risotto, 2x main dishes, 1x desserts';
                })()}
              </div> */}
            </div>
          <button
            className={`absolute bottom-4 right-4 text-white rounded-full w-10 h-10 flex items-center justify-center shadow-md ${
              pkg.isDisabled || !areFiltersComplete ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-900'
            }`}
            disabled={pkg.isDisabled || !areFiltersComplete}
            onClick={(e) => {
              e.stopPropagation(); // Prevent event bubbling
              handlePackageClick(pkg);
            }}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>          </button>
        </div>
      </div>
          ))}
      </div>

      {/* Confirmation Popup */}
      {showConfirmation && selectedPackageForConfirmation && (() => {
        const modalContent = getModalContent();
        if (!modalContent) return null;
        
        return (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
              <div className="flex items-start mb-4">
                {/* Warning icon */}
                <svg className="w-6 h-6 text-amber-500 mr-3 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <h3 className="text-lg font-bold text-gray-800 flex-1">
                  {modalContent.title}
                </h3>
              </div>
              <p className="text-gray-600 mb-6 ml-9">
                {modalContent.message}
              </p>
              <div className="flex space-x-3">
                <button
                  onClick={handleCancelPackageChange}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmPackageChange}
                  className={`flex-1 px-4 py-2 text-white rounded-lg transition-colors ${modalContent.confirmClass}`}
                >
                  {modalContent.confirmText}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Pending Payment Warning Modal */}
      <PendingPaymentWarningModal
        isOpen={showPendingPaymentWarning}
        onClose={handleCancelPendingPayment}
        onProceed={handleConfirmPendingPayment}
        actionDescription={`selecting "${selectedPackageForConfirmation?.title || 'this package'}"`}
        additionalContext="The current order will be cancelled and the cart will be reset."
      />
    </>
  );
};

export default PackageGrid; 